import React from "react";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import ContactForm from "../components/ContactUsForm/ContactUsForm";
import { Helmet } from "react-helmet";
import styles from "./Contact.module.css";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, x: -60 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.7, type: "spring" } },
};

const Contact = () => {
  const { scrollYProgress } = useScroll();
  const parallaxY = useTransform(scrollYProgress, [0, 1], [0, -120]);
  const springParallax = useSpring(parallaxY, { stiffness: 120, damping: 25 });

  return (
    <>
      <Helmet>
        <title>Contact Studio Maamangala | Wedding Photography in Baripada, Odisha</title>
        <meta
          name="description"
          content="Reach out to Studio Maamangala in Baripada, Odisha, for cinematic wedding photography, videography, and pre-wedding shoots."
        />
        <meta
          name="keywords"
          content="contact Studio Maamangala, wedding photographer Baripada, Odisha cinematography, pre-wedding shoots, Studio Maamangala contact"
        />
        <meta property="og:title" content="Contact Studio Maamangala" />
        <meta
          property="og:description"
          content="Connect with us for professional wedding photography and videography services in Baripada, Odisha."
        />
        <meta property="og:url" content="https://studiomaamangala.com/contact" />
        <meta property="og:type" content="website" />
        <meta
          property="og:image"
          content="https://studiomaamangala.com/wp-content/uploads/2025/02/michael-soledad-jiOByhCw2jE-unsplash-scaled.jpg"
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Contact Studio Maamangala | Baripada, Odisha" />
        <meta name="twitter:description" content="Get in touch for cinematic wedding photography and videography services." />
        <meta
          name="twitter:image"
          content="https://studiomaamangala.com/wp-content/uploads/2025/02/michael-soledad-jiOByhCw2jE-unsplash-scaled.jpg"
        />
      </Helmet>

      <motion.div className={styles.contactPage} initial="hidden" animate="visible" variants={containerVariants}>
        {/* Hero Section */}
        <section className={styles.hero}>
          <motion.div
            className={styles.heroOverlay}
            style={{ y: springParallax }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.85 }}
            transition={{ duration: 2 }}
          />
          <motion.img
            src="https://studiomaamangala.com/wp-content/uploads/2025/02/michael-soledad-jiOByhCw2jE-unsplash-scaled.jpg"
            alt="Studio Maamangala Wedding Scene"
            className={styles.heroImage}
            style={{ y: springParallax }}
            initial={{ scale: 1.3 }}
            animate={{ scale: 1 }}
            transition={{ duration: 2.5, type: "spring" }}
          />
          <motion.div className={styles.heroContent} variants={containerVariants}>
            <motion.h1 variants={itemVariants}>Connect With Us</motion.h1>
            <motion.p variants={itemVariants}>
              Let’s create cinematic memories for your wedding. Reach out to Studio Maamangala in Baripada, Odisha.
            </motion.p>
            <motion.a
              href="#contact-form"
              className={styles.ctaButton}
              variants={itemVariants}
              whileHover={{ scale: 1.15, boxShadow: "0 0 20px rgba(212, 160, 23, 0.6)" }}
              whileTap={{ scale: 0.85 }}
            >
              Get Started
            </motion.a>
          </motion.div>
        </section>

        {/* Contact Form Section */}
        <motion.section
          className={styles.formSection}
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          id="contact-form"
        >
          <motion.div className={styles.formContent}>
            <motion.h2 variants={itemVariants}>Send Us a Message</motion.h2>
            <motion.p variants={itemVariants}>
              Whether you’re planning a wedding, pre-wedding shoot, or event, we’re here to bring your vision to life. Fill out the form below, and we’ll respond promptly.
            </motion.p>
            <motion.div variants={itemVariants} className={styles.formWrapper}>
              <ContactForm />
            </motion.div>
          </motion.div>
        </motion.section>

        {/* Connect With Us Section */}
        <motion.section
          className={styles.connectSection}
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          <motion.div className={styles.connectContent}>
            <motion.h2 variants={itemVariants}>More Ways to Reach Us</motion.h2>
            <div className={styles.connectGrid}>
              <motion.div
                className={styles.connectCard}
                variants={itemVariants}
                whileHover={{ y: -10, boxShadow: "0 10px 20px rgba(212, 160, 23, 0.3)" }}
              >
                <h3>Email</h3>
                <a href="mailto:info@studiomaamangala.com" className={styles.connectLink}>
                  info@studiomaamangala.com
                </a>
              </motion.div>
              <motion.div
                className={styles.connectCard}
                variants={itemVariants}
                whileHover={{ y: -10, boxShadow: "0 10px 20px rgba(212, 160, 23, 0.3)" }}
              >
                <h3>Phone</h3>
                <a href="tel:+918328803919" className={styles.connectLink}>
                  +91 8328803919
                </a>
              </motion.div>
              <motion.div
                className={styles.connectCard}
                variants={itemVariants}
                whileHover={{ y: -10, boxShadow: "0 10px 20px rgba(212, 160, 23, 0.3)" }}
              >
                <h3>Address</h3>
                <p>Jharpokharia, Baripada, Odisha, India</p>
              </motion.div>
            </div>
          </motion.div>
        </motion.section>

        {/* Social Media Section */}
        <motion.section
          className={styles.socialSection}
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          <motion.div className={styles.socialContent}>
            <motion.h2 variants={itemVariants}>Follow Us</motion.h2>
            <motion.div className={styles.socialGrid} variants={containerVariants}>
              <motion.a
                href="https://instagram.com/maa_mangala_studio"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.socialLink}
                variants={itemVariants}
                whileHover={{ scale: 1.2, rotate: 5 }}
              >
                <span className={styles.socialIcon}>📸</span> Instagram
              </motion.a>
              <motion.a
                href="https://www.facebook.com/hemantasahu9583"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.socialLink}
                variants={itemVariants}
                whileHover={{ scale: 1.2, rotate: -5 }}
              >
                <span className={styles.socialIcon}>📘</span> Facebook
              </motion.a>
            </motion.div>
          </motion.div>
        </motion.section>
      </motion.div>
    </>
  );
};

export default Contact;